import { IS_NATIVE } from "../config.js";
import { loadApp } from "./plugins.js";

/**
 * Where the app's launch parameters come from.
 *
 * On the web that is `window.location.search`. Natively the document is always
 * `capacitor://localhost/` with no query at all — a link that opened the app arrives
 * through the App plugin instead, so `?layout=`/`?preset=` would otherwise be invisible.
 */

/** Cold start: the URL that launched the app, or the page URL on web. */
export async function resolveLaunchURL() {
  const here = new URL(window.location.href);
  if (!IS_NATIVE) return here;

  try {
    const { App } = await loadApp();
    const launch = await App.getLaunchUrl();
    if (launch?.url) return new URL(launch.url);
  } catch (error) {
    console.warn("[deeplink] launch URL unavailable:", error);
  }
  return here;
}

/**
 * Mirror the launch URL's query onto `window.location` so the readers that parse
 * `location.search` independently — localisation's `?country`/`?lang`, the layout modal —
 * agree with the params the app booted from. replaceState only, and same-origin, so it
 * cannot throw the way a cross-origin rewrite would.
 */
export function adoptLaunchParams(url) {
  if (!IS_NATIVE) return;
  const query = url.searchParams.toString();
  try {
    window.history.replaceState(null, "", query ? `?${query}` : window.location.pathname);
  } catch (error) {
    console.warn("[deeplink] could not adopt launch params:", error);
  }
}

/**
 * Links opened while the app is already running. Returns an unsubscribe function.
 * No-op on web, where a link simply navigates.
 */
export function onAppUrlOpen(handler) {
  if (!IS_NATIVE) return () => {};

  let remove = () => {};
  loadApp()
    .then(async ({ App }) => {
      const sub = await App.addListener("appUrlOpen", ({ url }) => {
        try {
          handler(new URL(url));
        } catch (error) {
          console.warn("[deeplink] bad url:", url, error);
        }
      });
      remove = () => sub.remove();
    })
    .catch((error) => console.warn("[deeplink] listener failed:", error));

  return () => remove();
}
